/**
 * PromoteItemToProductModal — promote an ad-hoc invoice/quote line item to a
 * permanent record in the Master Product Registry (collection: products).
 *
 * UX: opens with pre-filled fields derived from the current item row
 * (description → name, unit_price → selling_price, tax_rate → default_tax_rate).
 * The operator can review/adjust SKU, supplier price, type (goods / service)
 * before saving. On success the modal returns the freshly created product so
 * the caller can link product_id / product_sku to the line item.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { X, Loader2, Package } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRODUCT_TYPES = [
  { value: 'goods', label: 'Goods (physical product)' },
  { value: 'service', label: 'Service' },
];

// Build a slug-style SKU suggestion from a free-text description.
const buildSkuSuggestion = (text, type) => {
  const base = (text || 'item')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 24) || 'ITEM';
  const prefix = type === 'service' ? 'SVC' : 'PRD';
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `${prefix}-${base}-${rand}`;
};

const PromoteItemToProductModal = ({ isOpen, item, currency, onClose, onPromoted }) => {
  const { toast } = useToast();
  const token = localStorage.getItem('admin_token');
  const [saving, setSaving] = useState(false);

  const [productType, setProductType] = useState('service');
  const [form, setForm] = useState({
    sku: '',
    name: '',
    description: '',
    unit_of_measure: 'unit',
    default_tax_rate: 5,
    supplier_price: '',
    selling_price: 0,
    default_currency: 'AED',
  });

  // Re-initialise whenever the modal opens for a new item.
  useEffect(() => {
    if (!isOpen || !item) return;
    const desc = item.description_en || item.description || '';
    const inferredType = (item.unit || 'units').toLowerCase().includes('hour')
      || /service|consult|fee|retainer/i.test(desc)
      ? 'service' : 'goods';
    setProductType(inferredType);
    setForm({
      sku: buildSkuSuggestion(desc, inferredType),
      name: desc.slice(0, 120),
      description: '',
      unit_of_measure: item.unit || 'unit',
      default_tax_rate: Number(item.tax_rate) || 5,
      supplier_price: '',
      selling_price: Number(item.unit_price) || 0,
      default_currency: currency || 'AED',
    });
  }, [isOpen, item, currency]);

  // Re-suggest SKU when the operator switches the product type — only if the
  // current SKU is still our auto-suggestion (PRD- / SVC- prefix), never if
  // it was manually edited.
  const regenSku = (type) => {
    if (form.sku && (form.sku.startsWith('PRD-') || form.sku.startsWith('SVC-'))) {
      setForm((p) => ({ ...p, sku: buildSkuSuggestion(form.name || p.name, type) }));
    }
  };

  const margin = useMemo(() => {
    const sell = Number(form.selling_price) || 0;
    const cost = Number(form.supplier_price) || 0;
    if (sell <= 0 || cost <= 0) return null;
    return ((sell - cost) / cost) * 100;
  }, [form.selling_price, form.supplier_price]);

  if (!isOpen || !item) return null;

  const updateField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!form.sku.trim() || !form.name.trim()) {
      toast({ variant: 'destructive', title: 'Missing fields', description: 'SKU and Name are required.' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        sku: form.sku.trim().toUpperCase(),
        name: form.name.trim(),
        description: form.description || null,
        unit_of_measure: form.unit_of_measure || 'unit',
        product_type: productType,
        default_currency: form.default_currency || 'AED',
        default_tax_rate: Number(form.default_tax_rate) || 0,
        selling_price: Number(form.selling_price) || 0,
        supplier_price: form.supplier_price === '' ? null : Number(form.supplier_price),
      };
      const r = await axios.post(`${API_URL}/api/products/`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({
        title: 'Product saved to Master Registry',
        description: `${r.data.sku} — ${r.data.name}`,
      });
      onPromoted?.(r.data);
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Failed to create product';
      toast({ variant: 'destructive', title: 'Could not save product', description: String(detail) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      data-testid="promote-product-modal"
    >
      <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b bg-gradient-to-r from-[#198754] to-[#16a34a] text-white rounded-t-xl">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            <h3 className="font-bold">Save as Product / Service</h3>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="promote-product-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="text-xs text-gray-600 bg-emerald-50 border border-emerald-200 rounded-lg p-3 leading-relaxed">
            This will register the line item permanently in the <strong>Master Product Registry</strong> so
            it can be reused in future invoices, quotes and procurement, and feed margin/P&amp;L analytics
            automatically.
          </div>

          {/* Type toggle */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Type *</label>
            <div className="flex gap-2">
              {PRODUCT_TYPES.map((pt) => (
                <button
                  key={pt.value}
                  type="button"
                  onClick={() => { setProductType(pt.value); regenSku(pt.value); }}
                  className={`flex-1 px-3 py-2 border rounded-lg text-sm font-medium transition ${
                    productType === pt.value
                      ? 'bg-[#198754] text-white border-[#198754]'
                      : 'bg-white text-gray-700 hover:border-[#198754]'
                  }`}
                  data-testid={`promote-product-type-${pt.value}`}
                >
                  {pt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">SKU *</label>
              <input
                type="text"
                value={form.sku}
                onChange={(e) => updateField('sku', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm font-mono uppercase"
                data-testid="promote-product-sku"
                required
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Auto-suggested from description. Must be unique across the registry.
              </p>
            </div>

            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                data-testid="promote-product-name"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Unit of Measure</label>
              <input
                type="text"
                value={form.unit_of_measure}
                onChange={(e) => updateField('unit_of_measure', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                placeholder="unit / hour / kg / box…"
                data-testid="promote-product-unit"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Default Tax Rate (%)</label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={form.default_tax_rate}
                onChange={(e) => updateField('default_tax_rate', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                data-testid="promote-product-tax"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Supplier Price ({form.default_currency})
                <span className="text-gray-400"> · internal</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.supplier_price}
                onChange={(e) => updateField('supplier_price', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
                placeholder="(optional)"
                data-testid="promote-product-supplier-price"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Selling Price ({form.default_currency}) *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.selling_price}
                onChange={(e) => updateField('selling_price', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
                data-testid="promote-product-selling-price"
                required
              />
            </div>

            {margin !== null && (
              <div className="col-span-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs">
                Indicative margin: <strong className="text-amber-900">{margin.toFixed(2)}%</strong>{' '}
                (selling − supplier / supplier)
              </div>
            )}

            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Description (optional)</label>
              <textarea
                rows={2}
                value={form.description}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Long-form catalogue description shown in the product page…"
                className="w-full px-3 py-2 border rounded-lg text-sm"
                data-testid="promote-product-description"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button type="button" variant="outline" onClick={onClose} data-testid="promote-product-cancel">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#198754] hover:bg-[#16a34a]"
              data-testid="promote-product-submit"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Package className="w-4 h-4 mr-2" />}
              Save to Master Registry
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PromoteItemToProductModal;
